import React, { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle, AlertCircle, FileText, Brain, CheckSquare, BookOpen, ChevronRight, ClipboardCheck, Shield, PlayCircle } from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import TaskPreviewModal from '../components/TaskPreviewModal';

const EmployeeTaskDashboard = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Determine base path for navigation (preboarding vs regular employee)
    const isPreboarding = location.pathname.includes('/preboard/');
    const basePath = isPreboarding ? `/preboard/${token}/dashboard` : '/employee';

    const [tasks, setTasks] = useState({
        allActive: [],
        completed: [],
        pendingReview: []
    });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'todo');
    const [selectedTask, setSelectedTask] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    useEffect(() => {
        fetchTasks();
    }, []);

    const fetchTasks = async () => {
        try {
            const [allActive, completed, pendingReview] = await Promise.all([
                api.get('/tasks/employee/my-tasks?filter=all_active', {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                api.get('/tasks/employee/my-tasks?filter=completed', {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                api.get('/tasks/employee/my-tasks?filter=pending_review', {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            setTasks({
                allActive: allActive.data.tasks || [],
                completed: completed.data.tasks || [],
                pendingReview: pendingReview.data.tasks || []
            });
        } catch (error) {
            console.error('Error fetching tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTaskClick = async (task) => {
        // If task is completed, in progress (and started), or pending review, go directly to detail
        if (task.status === 'completed' || task.status === 'in_progress' || task.status === 'pending_review' || task.status === 'retake_requested') {
            navigate(`${basePath}/tasks/${task.id}`);
            return;
        }

        // Fetch full task details before opening preview to ensure complete data (sections, etc.)
        let taskToPreview = task;
        try {
            const response = await api.get(`/tasks/employee/assignment/${task.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success && response.data.task) {
                taskToPreview = response.data.task;
            }
        } catch (error) {
            console.error('Failed to fetch full task details for preview:', error);
            // Continue with summary data as fallback
        }

        // Otherwise (not_started, viewed), open preview modal
        setSelectedTask(taskToPreview);
        setIsPreviewOpen(true);

        // Mark as viewed in backend if not already (handle both 'pending' from DB and 'not_started')
        if (task.status === 'pending' || task.status === 'not_started') {
            try {
                await api.post(`/tasks/employee/${task.id}/track-view`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Optimistically update local state to 'viewed'
                updateTaskStatusLocally(task.id, 'viewed');
            } catch (error) {
                console.error('Failed to mark task as viewed:', error);
            }
        }
    };

    const updateTaskStatusLocally = (taskId, newStatus) => {
        const updateList = (list) => list.map(t => t.id === taskId ? { ...t, status: newStatus } : t);

        setTasks(prev => ({
            allActive: updateList(prev.allActive),
            completed: updateList(prev.completed),
            pendingReview: updateList(prev.pendingReview)
        }));
    };

    const handleStartTask = () => {
        if (!selectedTask) return;
        setIsPreviewOpen(false);
        navigate(`/employee/tasks/${selectedTask.id}`);
    };

    const getTaskIcon = (taskType) => {
        switch (taskType) {
            case 'quiz':
                return Brain;
            case 'training':
                return BookOpen;
            case 'checklist':
                return CheckSquare;
            default:
                return FileText;
        }
    };

    const getTaskIconColor = (taskType, isCompliance) => {
        if (isCompliance) return 'bg-blue-100 text-blue-700'; // Specific compliance color
        switch (taskType) {
            case 'quiz':
                return 'bg-purple-100 text-purple-600';
            case 'training':
                return 'bg-blue-100 text-blue-600';
            case 'checklist':
                return 'bg-green-100 text-green-600';
            default:
                return 'bg-primary/10 text-primary';
        }
    };


    const getStatusBadge = (task) => {
        if (task.status === 'completed') {
            return { text: 'Completed', color: 'text-green-500' };
        }
        if (task.status === 'retake_requested') {
            return { text: 'Retake Requested', color: 'text-orange-500' };
        }
        if (task.status === 'pending_review') {
            return { text: 'Pending Review', color: 'text-yellow-500' };
        }
        if (task.status === 'in_progress') {
            return { text: 'In Progress', color: 'text-blue-500' };
        }
        if (task.status === 'paused') {
            return { text: 'Paused', color: 'text-amber-500' };
        }
        // Check if overdue
        const now = new Date();
        const dueDate = new Date(task.due_date);
        if (dueDate < now) {
            return { text: 'Overdue', color: 'text-red-500' };
        }
        if (task.status === 'viewed') {
            return { text: 'Viewed', color: 'text-indigo-500' };
        }
        return { text: 'Not Started', color: 'text-muted-foreground' };
    };

    const formatDueDate = (dueDate) => {
        const date = new Date(dueDate);
        const options = { weekday: 'long', day: 'numeric', month: 'short' };
        const formattedDate = date.toLocaleDateString('en-US', options);
        const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        return { date: formattedDate, time };
    };

    const getTimeRemaining = (dueDate) => {
        const now = new Date();
        const date = new Date(dueDate);
        const diffMs = date - now;

        if (diffMs < 0) {
            const absDiff = Math.abs(diffMs);
            const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            if (days > 0) return `Overdue by ${days}d ${hours}h`;
            return `Overdue by ${hours}h`;
        }

        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (days > 0) return `Due in ${days}d ${hours}h`;
        if (hours > 0) return `Due in ${hours}h`;
        return 'Due soon';
    };

    // Format time spent (accumulated_duration is in milliseconds)
    const formatTimeSpent = (accumulatedDuration, startedAt) => {
        let totalMs = parseInt(accumulatedDuration) || 0;

        // Add current session time if task has started_at
        if (startedAt) {
            const now = new Date();
            const start = new Date(startedAt);
            totalMs += (now - start);
        }

        const totalMinutes = Math.floor(totalMs / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    // Get tasks for each tab
    const getTabTasks = () => {
        switch (activeTab) {
            case 'todo':
                return tasks.allActive;
            case 'done':
                return tasks.completed;
            case 'pending':
                return tasks.pendingReview;
            case 'missed': {
                const now = new Date();
                return tasks.allActive.filter(t => new Date(t.due_date) < now);
            }
            default:
                return [];
        }
    };

    // Group tasks by time period
    const groupTasksByPeriod = (taskList) => {
        const now = new Date();
        const endOfWeek = new Date(now);
        endOfWeek.setDate(now.getDate() + 7);

        const groups = {
            overdue: [],
            today: [],
            thisWeek: [],
            later: []
        };

        taskList.forEach(task => {
            const dueDate = new Date(task.due_date);
            const isToday = dueDate.toDateString() === now.toDateString();

            if (dueDate < now && !isToday) {
                groups.overdue.push(task);
            } else if (isToday) {
                groups.today.push(task);
            } else if (dueDate <= endOfWeek) {
                groups.thisWeek.push(task);
            } else {
                groups.later.push(task);
            }
        });

        return groups;
    };

    const getUrgencyLevel = (dueDate, status) => {
        if (status === 'completed') return 'normal';

        const now = new Date();
        const date = new Date(dueDate);
        const diffMs = date - now;
        const diffHours = diffMs / (1000 * 60 * 60);
        const diffDays = diffHours / 24;

        if (diffMs < 0) return 'overdue';
        if (diffHours < 24) return 'urgent'; // < 24 hours
        if (diffDays <= 3) return 'warning'; // 1-3 days
        return 'normal';
    };

    const TaskCard = ({ task }) => {
        const isCompliance = task.is_compliance_required;
        const IconComponent = isCompliance ? Shield : getTaskIcon(task.task_type);
        const iconColor = getTaskIconColor(task.task_type, isCompliance);
        const status = getStatusBadge(task);
        const dueInfo = formatDueDate(task.due_date);
        const timeRemaining = getTimeRemaining(task.due_date);

        const urgency = getUrgencyLevel(task.due_date, task.status);

        // Visual styles based on urgency
        const urgencyStyles = {
            overdue: { border: 'border-l-4 border-l-red-500', badge: 'bg-red-100 text-red-700', icon: AlertCircle },
            urgent: { border: 'border-l-4 border-l-orange-500', badge: 'bg-orange-100 text-orange-700', icon: Clock },
            warning: { border: 'border-l-4 border-l-yellow-400', badge: 'bg-yellow-100 text-yellow-700', icon: Clock },
            normal: { border: 'border-l-4 border-l-transparent', badge: 'bg-muted text-muted-foreground', icon: null }
        };

        let currentStyle = urgencyStyles[urgency];

        // Compliance override for normal urgency
        if (isCompliance && urgency === 'normal') {
            currentStyle = {
                ...currentStyle,
                border: 'border-l-4 border-l-blue-600'
            };
        }

        const UrgencyIcon = currentStyle.icon;
        const bgClass = isCompliance ? 'bg-blue-50/50' : 'bg-card';

        return (
            <div
                className={`${bgClass} rounded-xl border border-border shadow-sm overflow-hidden hover:shadow-md transition-all cursor-pointer group ${currentStyle.border}`}
                onClick={() => handleTaskClick(task)}
            >
                {/* Main Content Area */}
                <div className="p-4 sm:p-5">
                    {/* Top Row: Icon, Title, Status, Start Button */}
                    <div className="flex items-start gap-3 sm:gap-4">
                        {/* Task Icon */}
                        <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${iconColor} flex items-center justify-center`}>
                            <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>

                        {/* Title and Info */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate text-sm sm:text-base">
                                            {task.title}
                                        </h3>
                                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0 hidden sm:block" />
                                    </div>

                                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                                        {/* Compliance Badge - First & Prominent */}
                                        {isCompliance && (
                                            <span className="flex items-center gap-1 bg-blue-600 text-white px-2 py-0.5 rounded font-bold shadow-sm tracking-wide text-[10px] sm:text-xs">
                                                <Shield className="w-3 h-3 fill-current" />
                                                COMPLIANCE
                                            </span>
                                        )}

                                        {/* Urgency Badge */}
                                        {urgency !== 'normal' && (
                                            <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${currentStyle.badge}`}>
                                                {UrgencyIcon && <UrgencyIcon className="w-3 h-3" />}
                                                {urgency === 'overdue' ? 'Overdue' : urgency === 'urgent' ? 'Due Today' : 'Due Soon'}
                                            </span>
                                        )}

                                        <span className={`font-medium ${status.color}`}>
                                            ○ {status.text}
                                        </span>
                                        <span className="text-muted-foreground hidden sm:inline">•</span>

                                        <span className="text-muted-foreground capitalize">
                                            {task.task_type}
                                        </span>
                                    </div>
                                </div>

                                {/* Start Button - Desktop */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleTaskClick(task);
                                    }}
                                    className="hidden sm:flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-2.5 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium transition-colors text-sm shadow-sm hover:shadow"
                                >
                                    <PlayCircle className="w-4 h-4" />
                                    {task.status === 'completed' || task.status === 'pending_review' ? 'View' :
                                        task.status === 'in_progress' ? 'Continue' :
                                            task.status === 'paused' ? 'Resume' : 'Start'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Row: Due Date and Estimated Time */}
                    <div className="mt-4 pt-4 border-t border-border/50">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            {/* Due Date Info */}
                            <div className="flex items-center gap-4 sm:gap-6">
                                <div className="flex items-start gap-2">
                                    <Calendar className={`w-4 h-4 mt-0.5 ${urgency !== 'normal' ? 'text-red-400' : 'text-muted-foreground'}`} />
                                    <div>
                                        <p className={`text-xs sm:text-sm font-medium ${urgency !== 'normal' ? 'text-red-700' : 'text-foreground'}`}>
                                            {dueInfo.date} • {dueInfo.time}
                                        </p>
                                        <p className={`text-xs ${urgency === 'overdue' ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                                            {timeRemaining}
                                        </p>
                                    </div>
                                </div>

                                {/* Estimated Completion Time */}
                                {task.estimated_duration && (
                                    <div className="flex items-start gap-2">
                                        <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="text-xs sm:text-sm font-medium text-foreground">
                                                {task.estimated_duration}m
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                Est. time
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Time Spent - for in-progress or paused tasks */}
                                {(task.status === 'in_progress' || task.status === 'paused') && (
                                    <div className="flex items-start gap-2">
                                        <Clock className={`w-4 h-4 mt-0.5 ${task.status === 'paused' ? 'text-amber-500' : 'text-blue-500'}`} />
                                        <div>
                                            <p className={`text-xs sm:text-sm font-medium ${task.status === 'paused' ? 'text-amber-600' : 'text-blue-600'}`}>
                                                {formatTimeSpent(task.accumulated_duration, task.status === 'in_progress' ? task.started_at : null)}
                                            </p>
                                            <p className={`text-xs ${task.status === 'paused' ? 'text-amber-500' : 'text-blue-500'}`}>
                                                {task.status === 'paused' ? 'Paused' : 'Spent'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Start Button - Mobile */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleTaskClick(task);
                                }}
                                className="sm:hidden flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium transition-colors text-sm shadow-sm"
                            >
                                <PlayCircle className="w-4 h-4" />
                                {task.status === 'completed' || task.status === 'pending_review' ? 'View' :
                                    task.status === 'in_progress' ? 'Continue' :
                                        task.status === 'paused' ? 'Resume' : 'Start'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const TaskSection = ({ title, tasks }) => {
        if (tasks.length === 0) return null;

        return (
            <div className="mb-6">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    {title}
                </h3>
                <div className="space-y-3">
                    {tasks.map(task => (
                        <TaskCard key={task.id} task={task} />
                    ))}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="p-4 sm:p-6 mx-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
            </div>
        );
    }

    const tabTasks = getTabTasks();
    const groupedTasks = groupTasksByPeriod(tabTasks);

    // Tab configuration
    const now = new Date();
    const overdueCount = tasks.allActive.filter(t => new Date(t.due_date) < now).length;

    const tabs = [
        {
            id: 'todo',
            label: 'To do',
            icon: Clock,
            count: tasks.allActive.length
        },
        {
            id: 'done',
            label: 'Done',
            icon: CheckCircle,
            count: tasks.completed.length
        },
        {
            id: 'pending',
            label: 'Pending Review',
            icon: ClipboardCheck,
            count: tasks.pendingReview.length
        },
        {
            id: 'missed',
            label: 'Missed',
            icon: AlertCircle,
            count: overdueCount
        }
    ];

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-card border-b border-border">
                <div className="px-4 lg:px-6 py-4 sm:py-6">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground">My Tasks</h1>
                    <p className="text-sm text-muted-foreground mt-1">Track and complete your assigned tasks</p>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-card border-b border-border sticky top-0 z-10">
                <div className="px-4 lg:px-6">
                    <div className="flex overflow-x-auto scrollbar-hide -mb-px">
                        {tabs.map(tab => {
                            const IconComponent = tab.icon;
                            const isActive = activeTab === tab.id;

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-4 py-3 sm:py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${isActive
                                        ? 'border-primary text-primary'
                                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                        }`}
                                >
                                    <IconComponent className="w-4 h-4" />
                                    <span>{tab.label}</span>
                                    {tab.count > 0 && (
                                        <span className={`ml-1.5 px-2 py-0.5 text-xs rounded-full ${isActive
                                            ? 'bg-primary/10 text-primary'
                                            : 'bg-muted text-muted-foreground'
                                            }`}>
                                            {tab.count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Task Content */}
            <div className="px-4 lg:px-6 py-4 sm:py-6">
                {tabTasks.length === 0 ? (
                    <div className="bg-card rounded-xl border border-border p-8 sm:p-12 text-center">
                        {activeTab === 'todo' && (
                            <>
                                <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-400 mx-auto mb-4" />
                                <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">All caught up!</h3>
                                <p className="text-muted-foreground text-sm sm:text-base">You have no pending tasks right now.</p>
                            </>
                        )}
                        {activeTab === 'done' && (
                            <>
                                <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-4" />
                                <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">No completed tasks</h3>
                                <p className="text-muted-foreground text-sm sm:text-base">Tasks you complete will appear here.</p>
                            </>
                        )}
                        {activeTab === 'pending' && (
                            <>
                                <ClipboardCheck className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-4" />
                                <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">No pending reviews</h3>
                                <p className="text-muted-foreground text-sm sm:text-base">Tasks awaiting review will appear here.</p>
                            </>
                        )}
                        {activeTab === 'missed' && (
                            <>
                                <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-400 mx-auto mb-4" />
                                <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">No missed tasks</h3>
                                <p className="text-muted-foreground text-sm sm:text-base">Great job staying on top of your deadlines!</p>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        {activeTab === 'todo' && (
                            <>
                                {groupedTasks.overdue.length > 0 && (
                                    <TaskSection title="Overdue" tasks={groupedTasks.overdue} />
                                )}
                                {groupedTasks.today.length > 0 && (
                                    <TaskSection title="Due Today" tasks={groupedTasks.today} />
                                )}
                                {groupedTasks.thisWeek.length > 0 && (
                                    <TaskSection title="In Next 7 Days" tasks={groupedTasks.thisWeek} />
                                )}
                                {groupedTasks.later.length > 0 && (
                                    <TaskSection title="Coming Up" tasks={groupedTasks.later} />
                                )}
                            </>
                        )}

                        {(activeTab === 'done' || activeTab === 'pending' || activeTab === 'missed') && (
                            <div className="space-y-3">
                                {tabTasks.map(task => (
                                    <TaskCard key={task.id} task={task} />
                                ))}
                            </div>
                        )}
                    </>
                )}
                {/* Task Preview Modal */}
                <TaskPreviewModal
                    task={selectedTask}
                    isOpen={isPreviewOpen}
                    onClose={() => setIsPreviewOpen(false)}
                    onStart={handleStartTask}
                />
            </div>
        </div>
    );
};

export default EmployeeTaskDashboard;
